import { FilterModel } from '@ag-grid-community/core/dist/esm/es6/interfaces/iFilter';
import { IServerSideGetRowsParams } from '@ag-grid-community/core/dist/esm/es6/interfaces/iServerSideDatasource';
import { formatDate } from '@angular/common';
import { Injectable } from '@angular/core';
import { Apollo, ApolloBase, gql } from 'apollo-angular';
import { Observable, map, switchMap } from 'rxjs';
import { CurrentShijiUserInterface } from 'shared/interfaces/current-shiji-user.interface';
import { CurrentSupplierUserInterface } from 'shared/interfaces/current-supplier-user.interface';
import { GridService } from 'shared/interfaces/grid-service';
import { CachingService } from 'shared/services/caching.service';
import { UserApiService } from 'shared/services/userApi.service';
import { ListingAccessFilter } from '../interfaces/listing-access-filter';
import { ListingAccessGroup } from '../interfaces/listing-access-group.interface';

@Injectable({
    providedIn: 'root',
})
export class AccessGroupsService implements GridService {
    private apollo: ApolloBase;
    private apolloListings: ApolloBase;

    constructor(
        private apolloProvider: Apollo,
        private cachingService: CachingService,
        private userApiService: UserApiService,
    ) {
        this.apollo = this.apolloProvider.use('usersAPI');
        this.apolloListings = this.apolloProvider.use('listingAPI');
    }

    NAME = 'AccessGroupsGridService';

    public getRows(params: IServerSideGetRowsParams, pageNumber: number, pageSize: number): Observable<any> {
        return this.userApiService.getLoginInformation().pipe(
            switchMap((userData: CurrentSupplierUserInterface | CurrentShijiUserInterface) => {
                const supplierId = (userData as CurrentSupplierUserInterface)?.supplierId;

                const getListingAccessGroups = `
                    query getAccessGroups (
                        $groupName: String,
                        $companyName: String,
                        $createdAt: DateOnly,
                        $modifiedAt: DateOnly,
                        $orderDirection: OrderDirection,
                        $orderBy: String,
                    ) {
                        userListingAccess {
                            search(
                                searchSettings: {
                                    pageNumber: ${pageNumber},
                                    pageSize: ${pageSize},
                                    orderDirection: $orderDirection,
                                    orderBy: $orderBy
                                },
                                filtering: {
                                    ${supplierId ? `supplierId: "${supplierId}"` : ''},
                                    name: $groupName,
                                    companyName: $companyName,
                                    createdAt: $createdAt,
                                    modifiedAt: $modifiedAt
                                }
                            )  {
                                data {
                                    id,
                                    supplierId,
                                    name,
                                    users {
                                        id
                                    }
                                    entityTracking {
                                        createdBy,
                                        createdAt,
                                        modifiedBy,
                                        modifiedAt
                                    },
                                    ${!supplierId ? `, companyName` : ''}
                                },
                                totalCount
                            }
                        }
                    }
                `;

                return this.apollo
                    .query({
                        query: gql(getListingAccessGroups),
                        variables: {
                            orderBy: params.request.sortModel[0]?.colId,
                            orderDirection: params.request.sortModel[0]?.sort.toUpperCase(),
                            groupName: (params.request.filterModel as FilterModel)['name']?.filter,
                            companyName: (params.request.filterModel as FilterModel)['companyName']?.filter,
                            createdAt: (params.request.filterModel as FilterModel)['createdAt']?.filter
                                ? formatDate((params.request.filterModel as FilterModel)['createdAt']?.filter, 'yyyy-MM-dd', 'en-US')
                                : undefined,
                            modifiedAt: (params.request.filterModel as FilterModel)['modifiedAt']?.filter
                                ? formatDate((params.request.filterModel as FilterModel)['modifiedAt']?.filter, 'yyyy-MM-dd', 'en-US')
                                : undefined,
                        },
                        fetchPolicy: 'no-cache',
                    })
                    .pipe(
                        map((response: any) => {
                            return response.data.userListingAccess.search;
                        }),
                    );
            }),
        );
    }

    public deleteListingAccessGroupById(accessGroupId: string) {
        return this.apollo
            .mutate({
                mutation: gql`
                mutation deleteAccessGroup {
                    userListingAccess {
                        deleteListingAccessGroup(listingAccessGroupId: "${accessGroupId}") {
                            data
                            statusCode
                            message
                        }
                    }
                }
            `,
            })
            .pipe(
                map((response: any) => {
                    return response.data.userListingAccess.deleteListingAccessGroup.data;
                }),
            );
    }

    public getListingAccessFilter(listingAccessFilter: ListingAccessFilter): Observable<any> {
        const listingFilterQuery = `
                query searchListingAccess {
                    listings {
                        searchListingsAccess(
                            filtering: {
                                supplierId: "${listingAccessFilter.supplierId}"
                                ${listingAccessFilter.brandIds ? 'brandIds: [' + listingAccessFilter.brandIds.map((id) => `"${id}"`) + ']' : ''}
                                ${listingAccessFilter.countryIds ? 'countryIds: [' + listingAccessFilter.countryIds + ']' : ''}
                            }
                        ) {
                            data {
                                brands {
                                    id
                                    displayText
                                }
                                countries {
                                    id
                                    displayText
                                }
                                listings {
                                    id
                                    displayText
                                }
                            }
                            statusCode
                            message
                            totalCount
                        }
                    }
                }
            `;

        return this.apolloListings
            .query({
                query: gql(listingFilterQuery),
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    return response.data.listings.searchListingsAccess.data[0];
                }),
            );
    }

    public getListingAccessGroup(accessGroupId: string): Observable<any> {
        const listingAccessGroup = `
                query userListingAccess {
                    userListingAccess {
                        search(
                            filtering: {
                                searchTerm: "${accessGroupId}"
                            }
                        ) {
                            data {
                                id
                                supplierId
                                name
                                description
                                companyName
                                listingAccesses {
                                    id
                                    accessIdValue
                                    accessType
                                    displayName
                                }
                            }
                            totalCount
                        }
                    }
                }
            `;

        return this.apollo
            .query({
                query: gql(listingAccessGroup),
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    return response.data.userListingAccess.search.data[0];
                }),
            );
    }

    public getAccessGroupBySupplierId(supplierId: string): Observable<any> {
        const listingAccessGroup = `
                query userListingAccess {
                    userListingAccess {
                        search(
                        searchSettings: {
                                    pageSize: ${10000}
                                },
                            filtering: {
                                targetSupplierId: "${supplierId}"
                            }
                        ) {
                            data {
                                id
                                supplierId
                                name
                                description
                                companyName
                            }
                            totalCount
                        }
                    }
                }
            `;

        return this.apollo
            .query({
                query: gql(listingAccessGroup),
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    return response.data.userListingAccess.search.data;
                }),
            );
    }

    public createListingAccessGroup(accessGroup: ListingAccessGroup): Observable<any> {
        const createListingAccessGroup = `
                    mutation userListingAccess {
                        userListingAccess {
                            createListingAccessGroup(listingAccessGroup: {
                                ${accessGroup.supplierId ? 'supplierId: "' + accessGroup.supplierId + '"' : ''}
                                ${accessGroup.name ? 'name: "' + accessGroup.name + '"' : ''}
                                ${
                                    accessGroup.listingAccessesInput
                                        ? 'listingAccessesInput: [' +
                                          accessGroup.listingAccessesInput.map(
                                              (access) => `{accessIdValue: "${access.accessIdValue}", accessType: ${access.accessType} }`,
                                          ) +
                                          ']'
                                        : ''
                                }
                            }) {
                                data,
                                statusCode,
                                message
                            }
                        }
                    }
                `;

        return this.apollo
            .mutate({
                mutation: gql(createListingAccessGroup),
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    return response.data.userListingAccess.createListingAccessGroup;
                }),
            );
    }

    public updateListingAccessGroup(accessGroup: ListingAccessGroup): Observable<any> {
        const updateListingAccessGroup = `
                    mutation userListingAccess {
                        userListingAccess {
                            updateListingAccessGroup(listingAccessGroup: {
                                ${accessGroup.id ? `id: "${accessGroup.id}"` : ''}
                                ${accessGroup.name ? `name: "${accessGroup.name}"` : ''}
                                ${
                                    accessGroup.listingAccessesInput
                                        ? 'listingAccessesInput: [' +
                                          accessGroup.listingAccessesInput.map(
                                              (access) => `{accessIdValue: "${access.accessIdValue}", accessType: ${access.accessType} }`,
                                          ) +
                                          ']'
                                        : ''
                                }
                            }) {
                                data,
                                statusCode,
                                message
                            }
                        }
                    }
                `;

        return this.apollo
            .mutate({
                mutation: gql(updateListingAccessGroup),
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    return response.data.userListingAccess.updateListingAccessGroup;
                }),
            );
    }
}
